/**
 * The Match Debrief screen's shared vocabulary — its route, its strings, and the one piece of
 * arithmetic three screens have to agree on.
 *
 * Authority: `um-app/13-match-debrief.md` §3 and §4, and the Technical Delegate's rulings of
 * **11 September 2026** recorded in that specification's own §3.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS MODULE EXISTS AT ALL
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Three screens name the Match Debrief: the debrief screen itself, the capture screen whose
 * header offers the way in, and Today, which carries both the finished-match card and the
 * unreleased-responses nudge. If each held its own copy of the path, the entry rule and the
 * nudge sentence, the three would drift on the first change to any of them — and the first two
 * import from `UmTodayPage` already, so a helper living on either page would close a cycle.
 *
 * Everything here is a pure function over its arguments. There is no fetch, no state and no
 * React, which is also what makes each of them assertable on its own.
 */

import { MATCH_LENGTH_MINUTES, UMPIRE_CRITERIA, tidyCriterionDisplayName } from "@hockeytd/shared";

import type { AppointedUmpireBody, FixtureRowView } from "../../api/types.js";

/**
 * `/um/match/:tmsMatchDbId/debrief` — the route, built from the same two facts the capture
 * screen's route is built from.
 *
 * **The key is the TMS DATABASE match id, never the match number** — gate G-12, and the reason
 * `UmCapturePage` gives at length: `M07` is not unique across the two competitions and a
 * knockout renumbering would silently point the screen at another match.
 *
 * The competition travels in the query for the same reason it travels to capture: a note must
 * carry one (`note.competition_id` is NOT NULL) and a debrief response is a note. `from` names
 * where the reader came from, and it exists so the back arrow can name ITS OWN target rather
 * than guessing — gap J7 forbids a bare `‹`, and a screen reachable from two places cannot
 * honestly hard-code one destination.
 */
export function matchDebriefPath(
  tmsMatchDbId: number,
  competitionId: string,
  from?: "capture" | "today",
): string {
  const query = new URLSearchParams({ competition: competitionId });
  if (from !== undefined) query.set("from", from);
  return `/um/match/${String(tmsMatchDbId)}/debrief?${query.toString()}`;
}

/**
 * How long after its scheduled start a match is treated as finished — **100 minutes**.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IT MOVED TO THE SHARED PACKAGE IN RUN 74, AND IT IS RE-EXPORTED HERE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Run 73 defined the number here, because the only thing that needed it was this screen's
 * `Debrief` entry rule. **Ruling R-D6 gave the server the same arithmetic** — the nightly report
 * builds at the last fixture's scheduled start plus this number plus a configured delay
 * (`apps/api/src/debrief/report-schedule.ts`) — so the constant now lives in
 * `@hockeytd/shared`'s `match-clock.ts`, which both halves already import from, and is
 * re-exported from here unchanged so nothing that already named it has to move. Its twin is
 * named in that file's own note; there is exactly one definition and no mirror to keep in step.
 *
 * The reasoning — that this system holds no Full Time signal and why 100 is the honest
 * approximation — is recorded in full beside the constant.
 *
 * **It is never the only route.** The debrief screen's own matchday and match selectors reach
 * any match of any day, which is what makes a wrong guess here cost a tap rather than a screen:
 * a manager holding a debrief for a match this arithmetic has not caught up with picks it from
 * the selector, and the next morning's debrief for yesterday's match was always going to be
 * reached that way.
 */
export { MATCH_LENGTH_MINUTES } from "@hockeytd/shared";

/**
 * Whether a fixture has, on this reckoning, finished.
 *
 * **An unparseable or absent time answers `true`**, which is the generous branch this app takes
 * at every other degradation (`CapturePanel`'s roster fallback, and for the same stated reason):
 * the cost of showing the control early is a screen that says `No Released Notes Yet For This
 * Match`, and the cost of withholding it is a manager who cannot reach the debrief she is
 * sitting in. A read that failed must never be the thing that stops the work.
 *
 * The scheduled time is Tokyo's, because every time this application prints is (spec 00 §7.3).
 */
export function matchHasLikelyEnded(
  fixture: Pick<FixtureRowView, "date" | "time">,
  now: Date = new Date(),
): boolean {
  const started = Date.parse(`${fixture.date}T${fixture.time}:00+09:00`);
  if (Number.isNaN(started)) return true;
  return now.getTime() >= started + MATCH_LENGTH_MINUTES * 60_000;
}

/**
 * Spec 01 §2.7's nudge card, in the debrief's own words — `M07 Match Debrief — 4 Responses Not Yet
 * Released`.
 *
 * **The count is the reader's own and is computed on the reader's own device**, from her own
 * `GET /notes/mine`. That is §2.7's own constraint, and ruling R4″ left it standing: the number
 * of a manager's unreleased notes is hers, and no TD-reachable surface produces it.
 */
export function debriefNudgeLine(matchNo: string, count: number): string {
  return `${matchNo} Match Debrief — ${String(count)} ${
    count === 1 ? "Response" : "Responses"
  } Not Yet Released`;
}

/**
 * The receipt a **sent** release press gets on this screen — `Released — 3 Responses Now Visible
 * To The Panel`.
 *
 * It says `Responses` where the capture screen says `Notes`, because that is what this press
 * named: the composer above it writes debrief responses and nothing else, and a receipt reading
 * `3 Notes` on a screen with no notes on it would leave a manager wondering which three.
 * Everything else about it is the capture screen's receipt, including the undo suffix, which is
 * appended by the caller from the SERVER's own `undoUntil`.
 */
export function releasedResponsesReceipt(count: number): string {
  return `Released — ${String(count)} ${
    count === 1 ? "Response" : "Responses"
  } Now Visible To The Panel`;
}

/**
 * The quiet strip of finding 4 — `2 More Notes Released Since You Opened This · Refresh
 * Summary`.
 *
 * **It is a press and never an automatic swap.** Two managers are reading a text aloud with the
 * umpire opposite them; a paragraph that rewrote itself mid-sentence would be the screen taking
 * the room's attention for a change nobody asked for. The strip states the fact and offers the
 * press, and the text under it does not move until somebody makes it.
 */
export function moreNotesStrip(count: number): string {
  return `${String(count)} More ${
    count === 1 ? "Note" : "Notes"
  } Released Since You Opened This · Refresh Summary`;
}

/**
 * The provenance line beneath a summary — `As Of 16:42 · Built From 5 Released Notes`.
 *
 * The house convention, in one place. `at` is `null` on every state that carries no text, and
 * the line is then not drawn at all rather than stamped with the time of the read: a stamp is a
 * claim about when a text was made, and there is no text.
 */
export function summaryStamp(clock: string | null, noteCount: number): string | null {
  if (clock === null) return null;
  return `As Of ${clock} · Built From ${String(noteCount)} Released ${
    noteCount === 1 ? "Note" : "Notes"
  }`;
}

/** What {@link splitSummary} returns: the room's paragraphs, and the managers' closing one. */
export interface SplitSummary {
  /** What went well, then worth discussing. Shown in Room View and in My View alike. */
  body: string[];
  /** The `worth aligning` sentence, or null. **My View only** (ruling U1). */
  alignment: string | null;
}

/**
 * Separating the alignment sentence from the rest of an organised summary — ruling **U1**.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS IS A SPLIT ON PROSE, AND WHY IT ERRS THE WAY IT DOES
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Call 6 returns ONE plain-text answer — `MATCH_DEBRIEF_SUMMARY_REGISTER` forbids headings,
 * bullets, numbering and markdown of any kind, and asks for short paragraphs in a fixed order:
 * what went well, then worth discussing, then **one closing sentence about the alignment, and
 * only when the caller handed the model an alignment to write about**. So the text carries no
 * label this function could read, and the structure it does carry is positional.
 *
 * Two facts make the split safe rather than a guess:
 *
 *  1. **Whether there IS a closing sentence is decided in code, never by the model.**
 *     `alignments` on the body is `alignmentFlags`' own deterministic output — the same function
 *     the 21:30 pack's `⇄` flag uses — and when it is empty the prompt tells the model in as
 *     many words not to write one and not to go looking for one. An empty array therefore means
 *     every paragraph is room-safe, and nothing is withheld.
 *  2. **When there IS one, it is the LAST paragraph.** That is the order the register names and
 *     the order a debrief is held in.
 *
 * **The failure mode is one-directional, and that is the point.** If a model handed an alignment
 * declined to write the closing sentence, this function withholds a `worth discussing` paragraph
 * from Room View that could safely have been shown — the reader sees it the moment she switches
 * to My View, and it is on screen in full either way in the released notes beneath. The opposite
 * error — an alignment sentence rendered across the table because a paragraph count came out
 * wrong — is the one U1 exists to prevent, and this function cannot make it.
 */
export function splitSummary(
  summary: string,
  hasAlignment: boolean,
): SplitSummary {
  const paragraphs = summary
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.trim())
    .filter((paragraph) => paragraph !== "");

  if (!hasAlignment || paragraphs.length === 0) {
    return { body: paragraphs, alignment: null };
  }
  return {
    body: paragraphs.slice(0, -1),
    alignment: paragraphs[paragraphs.length - 1] ?? null,
  };
}

/* ── Run 74 — the vocabulary the daily report shares with this screen ──────── */

/**
 * The on-screen name of the responses section, in one place for the WHOLE application.
 *
 * **Moved here from `UmMatchDebriefPage` in run 74**, and only because a third and fourth
 * surface now print it: the Consolidated Daily UM Debrief Report on the phone and on paper
 * (spec 13 §8 — *"one heading in both places"*). `UmMatchDebriefPage` re-exports it unchanged,
 * so nothing that already named it has to move, and this module was already the place the three
 * screens agree on their words. It also has to live outside both pages: `UmMatchDebriefPage`
 * imports from `UmDebriefPage`, so a constant on either would close a cycle.
 *
 * **The STORED kind stays `debrief_response`** — the database column, the API's typed refusal
 * and migration 054's CHECK all keep that word, and nothing here renames data. What changes is
 * what the room reads: `Debrief Responses` is a schema word wearing a heading.
 */
export const RESPONSES_HEADING = "What The Umpire Said";

/**
 * **Ruling R-D5's strip**, in one place — and it is deliberately the same sentence the API's
 * `debrief/assembly.ts` exports, because the phone and the paper must not word the same ruling
 * two ways.
 */
export const ADDENDUM_STRIP = "Debrief Held After This Report Was Built";

/** `Debrief Held After This Report Was Built · Added 22:14`, or the strip alone when unstamped. */
export function addendumStripLine(addedAt: string | null): string {
  return addedAt === null ? ADDENDUM_STRIP : `${ADDENDUM_STRIP} · Added ${addedAt}`;
}

/**
 * The anchor a match section carries, and the jump index links to — **UX finding 22**.
 *
 * Built from the DISPLAY match number rather than the database id, because the thing a reader
 * sees in the index is `M07` and an anchor that read `#report-22889` would be a link whose
 * target nobody could recognise in an address bar. It is unique within one day's report, which
 * is the only scope an anchor has: `displayMatchNo` is already W-prefixed for the women's
 * competition precisely so that two competitions cannot both claim `M07`.
 *
 * Non-alphanumerics are replaced rather than stripped, so a match number that ever carried a
 * space or a slash still yields a legal id instead of two match numbers colliding on one.
 */
export function matchSectionAnchor(displayMatchNo: string): string {
  return `report-match-${displayMatchNo.replace(/[^A-Za-z0-9]+/g, "-")}`;
}

/* ── Run 75 — the vocabulary the TD's read-only screen shares with the UM's ── */

/**
 * **Why a second wave of lifting happened here, and where it stopped.**
 *
 * Run 75 built the Technical Delegate a read-only Match Debrief screen in the Control Centre
 * (`pages/TdMatchDebriefPage.tsx`). It draws the same three seats, the same organised summary and
 * the same released notes as the UM app's screen, from the same bytes — so everything below was
 * moved out of `UmMatchDebriefPage` and `UmDebriefPage` rather than copied into the new page.
 * Run 73's own note anticipated exactly this: *"its selectors could be lifted."*
 *
 * Each of them is re-exported from the module it came from, so no existing import had to change
 * and no test names a moved symbol through a new path.
 *
 * **What was deliberately NOT lifted**: every write path. The composer, `Release`, `Lock` and
 * `Undo Release` stay whole inside `UmMatchDebriefPage`, because the one property run 75 has to
 * keep true is that no write control exists on the TD's side to be reached, re-enabled or
 * imported by accident. A shared component with an `onSave` prop the TD page passes `undefined`
 * to would be that control, one careless line from being wired up.
 */

/**
 * `2026-09-22T13:14:00Z` → `22:14`. Tokyo, 24-hour, and `null` for anything unparseable.
 *
 * Tokyo because the tournament runs on Tokyo time (spec 00 §7.3, spec 11 §4): a stamp in the
 * reader's own zone would not line up with the day it sits beside. **Moved from `UmDebriefPage`
 * in run 75** and re-exported from there unchanged.
 */
export function tokyoClockTime(instant: string): string | null {
  const at = new Date(instant);
  if (Number.isNaN(at.getTime())) return null;
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Tokyo",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(at);
}

/**
 * `Tuesday 22 September` — a matchday, spelled out. **Moved from `UmTodayPage` in run 75.**
 *
 * The date is a plain calendar day and is formatted in UTC deliberately: `2026-09-22` is the
 * day's NAME, not an instant, and re-zoning it would move it.
 */
export function tokyoDateKicker(day: string): string {
  const parsed = new Date(`${day}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return day;
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "UTC",
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(parsed);
}

/**
 * `Matchday 4 · 21 September` — the matchday selector's label, on the phone and in the Control
 * Centre alike. **Moved from `UmDebriefPage` in run 75.**
 *
 * The matchday NUMBER is the day's position in the tournament's own list of days with fixtures,
 * computed from that list rather than stored: a rest day is not a matchday, and counting dates
 * would make it one.
 */
export function matchdayLabel(days: readonly string[], day: string): string {
  const index = days.indexOf(day);
  const named = tokyoDateKicker(day).replace(/^\w+ /, "");
  return index < 0 ? named : `Matchday ${index + 1} · ${named}`;
}

/**
 * The three seats a debrief is held with — **U1, U2 and VU, in this order, and never `RU`**.
 *
 * The reserve umpire does not attend the post-match debrief (spec 13 §1's own table), so the
 * reserve is filtered out of the appointment list rather than drawn and greyed: a card for
 * somebody who is not in the room would be a card two managers would eventually fill in. The
 * Technical Delegate's read-only screen holds to the same three, because it is a window onto the
 * same meeting and a fourth card there would describe a debrief nobody held.
 */
export const DEBRIEF_SEATS: ReadonlyArray<AppointedUmpireBody["slot"]> = ["U1", "U2", "VU"];

/** The slot codes in plain Technical-Delegate language. `CapturePanel`'s mapping, narrowed. */
export const SEAT_LABEL: Readonly<Record<"U1" | "U2" | "VU", string>> = {
  U1: "Umpire 1",
  U2: "Umpire 2",
  VU: "Video Umpire",
};

/** The seats of a match, in U1 · U2 · VU order, with the reserve dropped. */
export function debriefSeats(
  appointments: readonly AppointedUmpireBody[],
): readonly AppointedUmpireBody[] {
  return DEBRIEF_SEATS.map((slot) =>
    appointments.find((umpire) => umpire.slot === slot),
  ).filter((umpire): umpire is AppointedUmpireBody => umpire !== undefined);
}

/** `VAN BUNGE Coen` → `VC`. The avatar, when there is no photograph to draw. */
export function seatInitials(name: string): string {
  const letters = name
    .split(/\s+/)
    .filter((part) => part !== "")
    .map((part) => part[0] ?? "");
  return (letters[0] ?? "") + (letters[letters.length - 1] ?? "");
}

/**
 * The criterion's display name, from the shared vocabulary — never the stored stable key.
 *
 * A key is a database word (`decision_making`) and this row is read aloud in a room. A key the
 * active criteria set does not hold falls back to `Not Yet Categorised`, which is the same
 * bucket name the Report Studio's evidence pane uses for a note filed under none.
 */
export function criterionName(stableKey: string | null): string {
  if (stableKey === null) return "Not Yet Categorised";
  const criterion = UMPIRE_CRITERIA.find((entry) => entry.criterion_stable_key === stableKey);
  return criterion === undefined
    ? "Not Yet Categorised"
    : tidyCriterionDisplayName(criterion.name);
}

/** `Q2 · 24′`, `Q3`, or `Unstamped`. The capture screen's own stamp, for a released row. */
export function noteStamp(note: {
  quarter: number | null;
  matchMinute: number | null;
}): string {
  if (note.quarter === null && note.matchMinute === null) return "Unstamped";
  return [
    note.quarter === null ? "" : `Q${String(note.quarter)}`,
    note.matchMinute === null ? "" : `${String(note.matchMinute)}′`,
  ]
    .filter((part) => part !== "")
    .join(" · ");
}
